/* eslint-disable no-useless-escape */
/** @format */

import { NgForm, NgModelGroup, UntypedFormControl } from '@angular/forms';
import { ErrorMessageFeedService } from './error-message-feed.service';
import { FIELD_ERROR_MESSAGE } from '@cart-checkout/checkout/data-access/constants';
import { IValidations } from '@cart-checkout/checkout/data-access/interfaces';
import { Injectable } from '@angular/core';
import { Logger } from '@logger';
import { SinglePageCards } from '@cart-checkout/checkout/data-access/enums';
import { CheckoutAnalyticsService } from '@cart-checkout/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class ValidationService {
	/**
	 * @todo update RegEx patterns for use in Angular. angular appends ^ to the begging and $ to the end of every string passed to pattern validator.
	 * all \ need escaped as well
	 * formatted patterns = phone,name
	 */
	public validations: IValidations = {
		email: {
			regex: '\^\[a\-zA\-Z0\-9\!\#\$%&\'\*\+\/\=\?\^_`\{\|\}~\-\]\+\(\?\:\\\.\[a\-zA\-Z0\-9\!\#\$%&\'\*\+\/\=\?\^_`\{\|\}~\-\]\+\)\*@\(\?\:\[a\-zA\-Z0\-9\]\(\?\:\[a\-zA\-Z0\-9\-\]\*\[a\-zA\-Z0\-9\]\)\?\\\.\)\+\[a\-zA\-Z0\-9\]\(\?\:\[a\-zA\-Z0\-9\-\]\*\[a\-zA\-Z0\-9\]\)\?\$'
		},
		zip: {
			regex: '(^[0-9]{5}(?:-[0-9]{4})?$)'
		},
		password: {
			regex: '(?=.*\\d)(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\\s).{8,}'
		},
		postalCode: {
			regex: '(^[0-9]{5}(?:-[0-9]{4})?$)'
		},
		phone: {
			// Changed regex so phone numbers don't start with 0 or 1.
			regex: '(\\+\\d{1,2}\\s)?\\(?[2-9]{1}\\d{2}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}'
		},
		name: {
			regex: "(?!.*([\\'])\\1{1})(?![\\d\\s\\-\\']*$)[a-zA-Z\\d]{1}[a-zA-Z\\d\\s\\-\\']{1,}"
			// regex: '(?!.*([\\\'])\\1{1})(?![\\d\\s\\-\\]*$)[a-zA-Z\\d\\s\\-\\\'\\,]{1,}'
		},
		address: {
			regex: "[a-zA-Z0-9]{1}[a-zA-Z0-9 \\'\\-\\/\\.\\#]{1,}"
		},
		address2: {
			regex: "[a-zA-Z0-9 \\'\\-\\/\\.\\#]{1,}"
		},
		scorecard: {
			regex: '(^[a-zA-Z0-9]{12}$)'
		}
	};

	constructor(
		private logger: Logger,
		private errorMessageFeedService: ErrorMessageFeedService,
		private checkoutAnalyticsService: CheckoutAnalyticsService) {
	}

	/**
	 * validate
	 */
	public validate(type) {
		let validation;
		const types = Object.keys(this.validations);

		// if type matches return string value
		if (types.indexOf(type) !== -1) {
			validation = this.validations[type].regex;
		} else {
			validation = '';
			this.logger.debug('incorrect ValidationService Type passed');
		}

		return validation;
	}

	/**
	 * @description
	 * Takes a string and removes all commas
	 * @param address
	 */
	public removeAddrCommas(address: string) {
		if (address) {
			return address.replace(/,/g, '').replace(/^ +/g, '');
		}
	}

	/**
	 * @description This method will recursivly validate a FormGroup's controls.
	 */
	public validateForm(form: NgForm, parentName: string) {
		let isFormValid = true;
		Object.getOwnPropertyNames(form.controls)?.forEach((name) => {
			const control = form.controls[name];
			if (control instanceof UntypedFormControl) {
				this.validateControl(control);

				if (control.invalid) {
					this.handleControlError(control, `${parentName}_${name}`);
					isFormValid = false;
				}
			}
		});
		// add analytics call for successful validation
		if(isFormValid) {
			this.checkoutAnalyticsService.triggerCheckoutActionEvent(`FormGroup:${ parentName }:${form.status}`);
		}
	}

	public validateModelGroup(modelGroup: NgModelGroup, parentName: string, isGifting = false, preventErrorFeedMessage = false) {
		let isFormValid = true;

		if(modelGroup && modelGroup.control) {
			Object.getOwnPropertyNames(modelGroup.control?.controls)?.forEach((name) => {
				const control = modelGroup.control?.controls[name];
				if (control instanceof UntypedFormControl) {
					this.validateControl(control);

					if (control.invalid) {
						let controlErrorKey = `${parentName}_${name}`;

						if(isGifting) {
							controlErrorKey += '_gift';
						}

						this.handleControlErrorForPage(control, controlErrorKey ,'SinglePage', preventErrorFeedMessage);
						isFormValid = false;
					}
				}
			});
		}
		// add analytics call for successful validation
		if(isFormValid) {
			this.checkoutAnalyticsService.triggerCheckoutActionEvent(`FormGroup:Contact:${modelGroup.status}`);
		}
	}

	/**
	 * @description This will force validation on a FormControl by
	 * calling markAsTouched and updateValueAndValidity on it.
	 *
	 * @param control - FormControl to validate
	 */
	public validateControl(control: UntypedFormControl) {
		control.markAsTouched();
		control.updateValueAndValidity({
			onlySelf: false,
			emitEvent: true
		});
	}

	/**
	 * @description This method will handle the first error on a given FormControl by finding the error message for
	 * it with the controlErrorKey and pushing it to the ErrorMessageFeedService.
	 *
	 * @param controlErrorKey - This is a special key that is used to find the appropriate error message
	 * for the given control.
	 */
	public handleControlError(control: UntypedFormControl, controlErrorKey: string) {
		const controlErrorMessages = FIELD_ERROR_MESSAGE[controlErrorKey];
		const controlError = Object.keys(control.errors)[0];

		if (controlErrorMessages && controlErrorMessages[controlError]) {
			this.errorMessageFeedService.push(controlErrorMessages[controlError]);
		}
	}

	public handleControlErrorForPage(control: UntypedFormControl, controlErrorKey: string, pageName: string, preventErrorFeedMessage: boolean) {
		const controlErrorMessages = FIELD_ERROR_MESSAGE[controlErrorKey];

		const controlError = Object.keys(control.errors)[0];

		const messageLocation = this.getMessageLocation(controlErrorKey);

		if (controlErrorMessages && controlErrorMessages[controlError]) {
			if (!preventErrorFeedMessage) {
				this.errorMessageFeedService.push(controlErrorMessages[controlError], null, null, messageLocation);
			}
			this.buildAndSendAnalyticsError(controlErrorMessages[controlError], `Checkout:${pageName}:FormGroup:${controlErrorKey.toUpperCase()}_ERROR`);
		}
	}

	private buildAndSendAnalyticsError(err: any, identifier: string): void {
		this.checkoutAnalyticsService.setErrorId(identifier);
		this.checkoutAnalyticsService.setErrorMessage(`CART:${err.message}`);
		this.checkoutAnalyticsService.triggerAnalyticsEvent(identifier, {
			ErrorMessage: `CART:${err.message}`
		});
	}

	private getMessageLocation(controlErrorKey: string): SinglePageCards | null {
		if (controlErrorKey.includes('billing') && !controlErrorKey.includes('newSavedAddress')) {
			return SinglePageCards.BILLING;
		} else if (controlErrorKey.includes('shipping') && !controlErrorKey.includes('newSavedAddress')) {
			return SinglePageCards.SHIPPING;
		} else if (controlErrorKey.includes('contact')) {
			return SinglePageCards.CONTACT_INFO;
		} else if (controlErrorKey.includes('gifting')) {
			return SinglePageCards.GIFTING;
		} else if (controlErrorKey.includes('newSavedAddress')) {
			return SinglePageCards.NEW_SAVED;
		} else {
			return null;
		}
	}
}
